import React from "react";
import { createPortal } from "react-dom";
import { InfiniteData, QueryClient, useQueryClient } from "@tanstack/react-query";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";

import { useSelector } from "react-redux";

import type {
  StoriesPage,
  StoryContent,
  StorySummary,
} from "../../@types/portalAluno";
import {
  registerStoryContentView,
  toggleStoryContentLike,
} from "../../services/portalAluno/stories/storiesService";
import useStoryContents from "../../hooks/PortalAluno/useStoryContents";
import { portalStoryContentsQueryKey } from "../../hooks/PortalAluno/useStoryContents";
import { portalStoriesQueryKey } from "../../hooks/PortalAluno/useHomeStories";
import type { ApplicationState } from "../../store";
import "./styles.css";

const STATIC_MEDIA_DURATION_MS = 6000;

const TIMED_MEDIA_TYPES = ["video", "audio"] as const;

type ContentPosition = "first" | "last" | "resume";

interface StoryOverlayProps {
  stories: StorySummary[];
  initialStoryIndex: number;
  onClose: () => void;
}

const isTimedMedia = (content?: StoryContent) =>
  Boolean(
    content &&
      (TIMED_MEDIA_TYPES as readonly string[]).includes(content.mediaType)
  );

const runsOnTimer = (content?: StoryContent) =>
  content?.mediaType === "image" || content?.mediaType === "pdf";

const getResumeIndex = (contents: StoryContent[]) => {
  const firstUnseen = contents.findIndex((content) => !content.seen);
  return firstUnseen >= 0 ? firstUnseen : 0;
};

const getContentPositionIndex = (
  contents: StoryContent[],
  position: ContentPosition
) => {
  if (contents.length === 0) return 0;
  if (position === "last") return contents.length - 1;
  if (position === "first") return 0;

  return getResumeIndex(contents);
};

export const updateStorySeenCache = (
  queryClient: QueryClient,
  idUsuario: number | undefined,
  storyId: number,
  storyContentId: number,
  seen: boolean
) => {
  let isStorySeen = false;
  let hasCachedContents = false;

  queryClient.setQueryData<StoryContent[]>(
    portalStoryContentsQueryKey(idUsuario, storyId),
    (currentContents) => {
      if (!currentContents) return currentContents;

      hasCachedContents = true;
      const nextContents = currentContents.map((item) =>
        item.id === storyContentId ? { ...item, seen } : item
      );
      isStorySeen =
        nextContents.length > 0 && nextContents.every((item) => item.seen);

      return nextContents;
    }
  );

  if (!hasCachedContents) return;

  queryClient.setQueryData<InfiniteData<StoriesPage>>(
    portalStoriesQueryKey(idUsuario),
    (currentStories) => {
      if (!currentStories) return currentStories;

      return {
        ...currentStories,
        pages: currentStories.pages.map((page) => ({
          ...page,
          stories: page.stories.map((item) =>
            item.id === storyId ? { ...item, seen: isStorySeen } : item
          ),
        })),
      };
    }
  );
};

const StoryOverlay = ({
  stories,
  initialStoryIndex,
  onClose,
}: StoryOverlayProps) => {
  const queryClient = useQueryClient();
  const idUsuario = useSelector(
    (state: ApplicationState) => state.auth.data?.idUsuario
  );
  const [storyIndex, setStoryIndex] = React.useState(initialStoryIndex);
  const [contentIndex, setContentIndex] = React.useState(0);
  const [positionedStoryId, setPositionedStoryId] = React.useState<number | null>(
    null
  );
  const [mediaProgress, setMediaProgress] = React.useState(0);
  const [likedContents, setLikedContents] = React.useState<
    Record<number, boolean>
  >({});
  const pendingPosition = React.useRef<ContentPosition>("resume");
  const viewedContentIds = React.useRef(new Set<number>());

  const story = stories[storyIndex];
  const { data, isError, isLoading, refetch } = useStoryContents(story?.id);
  const contents = React.useMemo(() => data ?? [], [data]);
  const content = positionedStoryId === story?.id ? contents[contentIndex] : undefined;
  const contentId = content?.id;
  const isLiked = content ? likedContents[content.id] ?? content.liked : false;

  const goToStory = React.useCallback(
    (nextIndex: number, position: ContentPosition) => {
      if (nextIndex < 0 || nextIndex > stories.length - 1) {
        onClose();
        return;
      }

      pendingPosition.current = position;
      setPositionedStoryId(null);
      setContentIndex(0);
      setStoryIndex(nextIndex);
    },
    [onClose, stories.length]
  );

  const goToNextContent = React.useCallback(() => {
    if (contentIndex < contents.length - 1) {
      setContentIndex((current) => current + 1);
      return;
    }

    goToStory(storyIndex + 1, "first");
  }, [contentIndex, contents.length, goToStory, storyIndex]);

  const goToPreviousContent = React.useCallback(() => {
    if (contentIndex > 0) {
      setContentIndex((current) => current - 1);
      return;
    }

    goToStory(storyIndex - 1, "last");
  }, [contentIndex, goToStory, storyIndex]);

  const goToNextContentRef = React.useRef(goToNextContent);

  React.useEffect(() => {
    goToNextContentRef.current = goToNextContent;
  });

  React.useLayoutEffect(() => {
    if (!story || contents.length === 0 || positionedStoryId === story.id) return;

    setContentIndex(getContentPositionIndex(contents, pendingPosition.current));
    pendingPosition.current = "resume";
    setPositionedStoryId(story.id);
  }, [contents, positionedStoryId, story]);

  React.useEffect(() => {
    setMediaProgress(0);
  }, [contentId]);

  React.useEffect(() => {
    if (!story || !content || viewedContentIds.current.has(content.id)) return;

    viewedContentIds.current.add(content.id);
    updateStorySeenCache(
      queryClient,
      idUsuario,
      story.id,
      content.id,
      true
    );
    registerStoryContentView(content.id).catch(() => {
      viewedContentIds.current.delete(content.id);
      updateStorySeenCache(
        queryClient,
        idUsuario,
        story.id,
        content.id,
        false
      );
    });
  }, [content, idUsuario, queryClient, story]);

  React.useEffect(() => {
    if (!runsOnTimer(content)) return undefined;

    const timeout = window.setTimeout(
      () => goToNextContentRef.current(),
      STATIC_MEDIA_DURATION_MS
    );

    return () => window.clearTimeout(timeout);
  }, [contentId, content?.mediaType, story?.id]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") goToNextContent();
      if (event.key === "ArrowLeft") goToPreviousContent();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNextContent, goToPreviousContent, onClose]);

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleTimeUpdate = React.useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>) => {
      const media = event.currentTarget;

      if (!Number.isFinite(media.duration) || media.duration <= 0) return;

      setMediaProgress(Math.min(media.currentTime / media.duration, 1));
    },
    []
  );

  const handleMediaEnded = React.useCallback(() => {
    setMediaProgress(1);
    goToNextContentRef.current();
  }, []);

  const handleLikeClick = () => {
    if (!content) return;

    setLikedContents((current) => ({ ...current, [content.id]: !isLiked }));
    toggleStoryContentLike(content.id).catch(() =>
      setLikedContents((current) => ({ ...current, [content.id]: isLiked }))
    );
  };

  const renderMedia = () => {
    if (isLoading) {
      return (
        <div className="story-overlay__state" role="status">
          Carregando story…
        </div>
      );
    }

    if (isError) {
      return (
        <div className="story-overlay__state" role="alert">
          <p>Não foi possível carregar este story.</p>
          <button type="button" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      );
    }

    if (!content) {
      return (
        <div className="story-overlay__state" role="status">
          Este story ainda não tem conteúdo publicado.
        </div>
      );
    }

    if (content.mediaType === "image") {
      return (
        <img
          className="story-overlay__media"
          src={content.mediaUrl}
          alt={content.title || story.title}
        />
      );
    }

    if (content.mediaType === "video") {
      return (
        <video
          className="story-overlay__media"
          key={content.id}
          src={content.mediaUrl}
          controls
          autoPlay
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleMediaEnded}
        />
      );
    }

    if (content.mediaType === "audio") {
      return (
        <div className="story-overlay__audio">
          <h3>{content.title || story.title}</h3>
          <audio
            key={content.id}
            src={content.mediaUrl}
            controls
            autoPlay
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleMediaEnded}
          />
        </div>
      );
    }

    return (
      <iframe
        className="story-overlay__media story-overlay__media--frame"
        key={content.id}
        src={content.mediaUrl}
        title={content.title || story.title}
      />
    );
  };

  if (!story) return null;

  return createPortal(
    <div
      className="story-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Story ${story.title}`}
    >
      <div className="story-overlay__backdrop" onClick={onClose} />

      <div className="story-overlay__panel">
        <div className="story-overlay__progress">
          {contents.map((item, index) => {
            const isDone = index < contentIndex;
            const isActive = index === contentIndex;
            const isTimedByDefault = isActive && runsOnTimer(item);
            const followsMedia = isActive && isTimedMedia(item);

            return (
              <span className="story-overlay__progress-track" key={item.id}>
                <span
                  className={`story-overlay__progress-bar${
                    isDone ? " story-overlay__progress-bar--done" : ""
                  }${isActive ? " story-overlay__progress-bar--active" : ""}${
                    isTimedByDefault ? " story-overlay__progress-bar--timed" : ""
                  }`}
                  style={
                    isTimedByDefault
                      ? { animationDuration: `${STATIC_MEDIA_DURATION_MS}ms` }
                      : followsMedia
                      ? { transform: `scaleX(${mediaProgress})` }
                      : undefined
                  }
                />
              </span>
            );
          })}
        </div>

        <header className="story-overlay__header">
          <div className="story-overlay__identity">
            {story.imageUrl && (
              <img
                src={story.imageUrl}
                alt=""
                className="story-overlay__avatar"
              />
            )}
            <div>
              <strong>{story.title}</strong>
              {content?.title && <span>{content.title}</span>}
            </div>
          </div>

          <button
            type="button"
            className="story-overlay__close"
            onClick={onClose}
            aria-label="Fechar story"
          >
            <CloseRoundedIcon aria-hidden="true" />
          </button>
        </header>

        <div className="story-overlay__stage">
          <button
            type="button"
            className="story-overlay__arrow story-overlay__arrow--previous"
            onClick={goToPreviousContent}
            aria-label="Story anterior"
          >
            <ChevronLeftRoundedIcon aria-hidden="true" />
          </button>

          <div className="story-overlay__viewer">{renderMedia()}</div>

          <button
            type="button"
            className="story-overlay__arrow story-overlay__arrow--next"
            onClick={goToNextContent}
            aria-label="Próximo story"
          >
            <ChevronRightRoundedIcon aria-hidden="true" />
          </button>
        </div>

        <footer className="story-overlay__footer">
          <span className="story-overlay__counter">
            {contents.length > 0
              ? `${contentIndex + 1} de ${contents.length}`
              : ""}
          </span>

          {content && (
            <button
              type="button"
              className={`story-overlay__like${
                isLiked ? " story-overlay__like--active" : ""
              }`}
              onClick={handleLikeClick}
              aria-pressed={isLiked}
              aria-label={isLiked ? "Remover curtida" : "Curtir conteúdo"}
            >
              <ThumbUpRoundedIcon aria-hidden="true" />
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default StoryOverlay;
